
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrushIcon from "@mui/icons-material/Brush";
import GroupsIcon from "@mui/icons-material/Groups";
import PaidIcon from "@mui/icons-material/Paid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CodeIcon from "@mui/icons-material/Code";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import PublicIcon from "@mui/icons-material/Public";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SchoolIcon from "@mui/icons-material/School";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PaletteIcon from "@mui/icons-material/Palette";

const MotionBox = motion(Box);

const BRAND = {
  turquoise: "#00B4A6",
  turquoiseDark: "#008D83",
  turquoiseSoft: "#DDF9F6",
  turquoisePale: "#F1FFFD",
  ink: "#102A2A",
  muted: "#5D7373",
  border: "#D6F0ED",
  cream: "#FFFDF8",
  gold: "#F7C948",
  violet: "#6366F1",
  violetSoft: "#EAE8FF",
  coral: "#FF7A7A",
  white: "#FFFFFF",
  subtle: "#F8FCFC",
};

const APPLICATION_ENDPOINT = "/api/opportunity-subscriptions/";

const roleOptions = [
  "Artist research",
  "Artist outreach",
  "Market research",
  "Social media and content",
  "AI and engineering ideas",
  "UX and website improvement",
  "Not sure yet",
];

const availabilityOptions = [
  "2 to 4 hours per week",
  "4 to 6 hours per week",
  "6 to 8 hours per week",
  "Flexible",
];

const locationOptions = ["London", "UK", "Remote", "Hybrid London / Remote"];

const missionCards = [
  {
    icon: <ManageSearchIcon />,
    title: "Find emerging artists",
    text: "Research painters, illustrators, digital artists and creative students who may want more visibility online.",
    outcome: "Artist lead list",
  },
  {
    icon: <CampaignIcon />,
    title: "Contact artists professionally",
    text: "Send friendly approved messages, explain the founding artist opportunity and track replies clearly.",
    outcome: "Outreach tracker",
  },
  {
    icon: <UploadFileIcon />,
    title: "Help artists upload",
    text: "Support interested artists so they can upload their paintings, complete their details and share their work.",
    outcome: "Qualified artist uploads",
  },
  {
    icon: <PsychologyIcon />,
    title: "Understand artist needs",
    text: "Ask what artists need, what would make them use the platform and what tools would actually help them.",
    outcome: "Research summary",
  },
];

const ideaCards = [
  {
    icon: <AutoAwesomeIcon />,
    title: "AI artwork descriptions",
    text: "Turn an uploaded painting into a professional, poetic artwork description.",
  },
  {
    icon: <PublicIcon />,
    title: "Artist profile pages",
    text: "Help artists create shareable public pages for their work.",
  },
  {
    icon: <BrushIcon />,
    title: "Digital certificate previews",
    text: "Create early certificate previews for paintings uploaded by founding artists.",
  },
  {
    icon: <CodeIcon />,
    title: "Engineering ideas welcome",
    text: "Students can suggest AI tools, upload flows, dashboards, search, recommendation systems or automation.",
  },
  {
    icon: <FavoriteIcon />,
    title: "Meaningful art experiences",
    text: "Explore how art can connect to rooms, gifts, birthdays, moods and stories.",
  },
  {
    icon: <RocketLaunchIcon />,
    title: "Startup portfolio work",
    text: "Build something real enough to discuss in interviews, portfolios or LinkedIn posts.",
  },
];

const timeline = [
  {
    label: "Week 1",
    title: "Research",
    text: "Find emerging artists, art students, painters and creative communities.",
  },
  {
    label: "Week 2",
    title: "Outreach",
    text: "Send polite messages and learn which artist groups respond best.",
  },
  {
    label: "Week 3",
    title: "Upload support",
    text: "Help interested artists upload their paintings and complete their profiles.",
  },
  {
    label: "Week 4",
    title: "Report",
    text: "Summarise what artists need and what Art For My Room should build next.",
  },
];

const starterForm = {
  fullName: "",
  email: "",
  university: "",
  course: "",
  roleInterest: "Artist research",
  availability: "4 to 6 hours per week",
  location: "London",
  portfolio: "",
  experience: "",
  aiIdea: "",
  whyJoin: "",
  rightToWork: false,
  consent: false,
};

function buildNotes(form) {
  return `
WORK WITH US APPLICATION - STUDENT ARTIST MISSION

Applicant:
Name: ${form.fullName}
Email: ${form.email}
University: ${form.university || "Not provided"}
Course: ${form.course || "Not provided"}
Location: ${form.location || "Not provided"}
Availability: ${form.availability || "Not provided"}

Mission interest:
${form.roleInterest || "Not provided"}

Portfolio / LinkedIn / Instagram / GitHub:
${form.portfolio || "Not provided"}

Relevant experience:
${form.experience || "Not provided"}

AI / engineering / art idea:
${form.aiIdea || "Not provided"}

Why they want to join:
${form.whyJoin || "Not provided"}

Confirmations:
Right to work information available if selected: ${form.rightToWork ? "Yes" : "No"}
Consent to be contacted about this opportunity: ${form.consent ? "Yes" : "No"}

Source:
Work With Us page on Art For My Room
`;
}

export default function StudentArtistMissionPage() {
  const [form, setForm] = useState(starterForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  const formReady = useMemo(() => {
    return (
      form.fullName.trim() &&
      form.email.trim() &&
      form.university.trim() &&
      form.roleInterest.trim() &&
      form.rightToWork &&
      form.consent
    );
  }, [form]);

  useEffect(() => {
    document.title =
      "Work With Us | Paid Student Artist Mission | Art For My Room";

    const setMeta = (selector, attrName, attrValue, content) => {
      let el = document.querySelector(`${selector}[${attrName}="${attrValue}"]`);

      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }

      el.setAttribute("content", content);
    };

    setMeta(
      "meta",
      "name",
      "description",
      "Apply for a paid student mission with Art For My Room. Help research artists, contact painters, support artwork uploads and suggest AI or engineering ideas for art."
    );

    setMeta(
      "meta",
      "property",
      "og:title",
      "Work With Us | Paid Student Artist Mission | Art For My Room"
    );

    setMeta(
      "meta",
      "property",
      "og:description",
      "A paid student opportunity to help emerging artists upload paintings to Art For My Room while building market research, outreach, AI and startup experience."
    );
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(starterForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage({ type: "", text: "" });

    if (!form.fullName.trim() || !form.email.trim()) {
      setSubmitMessage({
        type: "error",
        text: "Please add your name and email.",
      });
      return;
    }

    if (!form.university.trim()) {
      setSubmitMessage({
        type: "error",
        text: "Please add your university or education provider.",
      });
      return;
    }

    if (!form.rightToWork) {
      setSubmitMessage({
        type: "error",
        text: "Please confirm that you can provide right-to-work information if selected.",
      });
      return;
    }

    if (!form.consent) {
      setSubmitMessage({
        type: "error",
        text: "Please confirm that Art For My Room can contact you about this opportunity.",
      });
      return;
    }

    setSubmitting(true);

    const payload = {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      organisation: form.university.trim() || "Student applicant",
      role: `Work With Us applicant - ${form.roleInterest}`,
      region: form.location || "London",

      // These fields match the same structure used by your existing email endpoint.
      industries: ["AI", "Creative Tech", "Public Art"],

      // Kept as "Discovery Tool" for compatibility with your existing opportunity endpoint.
      // The real application details are included inside notes.
      opportunityTypes: ["Discovery Tool"],

      notes: buildNotes(form),
    };

    try {
      const response = await fetch(APPLICATION_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || "Unexpected server response." };
      }

      if (!response.ok) {
        throw new Error(data.message || "Could not send your application.");
      }

      setSubmitMessage({
        type: "success",
        text:
          data.message ||
          "Your application has been sent successfully. Thank you for applying.",
      });

      resetForm();
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text:
          error.message ||
          "Could not send your application right now. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BRAND.white }}>
      <Box
        sx={{
          background: `
            radial-gradient(circle at top left, ${alpha(BRAND.turquoise, 0.18)}, transparent 34%),
            radial-gradient(circle at bottom right, ${alpha(BRAND.violet, 0.12)}, transparent 32%),
            linear-gradient(180deg, ${BRAND.turquoisePale} 0%, #FFFFFF 76%)
          `,
          borderBottom: `1px solid ${alpha(BRAND.turquoise, 0.16)}`,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <MotionBox
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
              >
                <Chip
                  icon={<PaidIcon />}
                  label="Paid student mission"
                  sx={{
                    mb: 2.5,
                    backgroundColor: "#FFFFFF",
                    color: BRAND.turquoiseDark,
                    border: `1px solid ${alpha(BRAND.turquoise, 0.24)}`,
                    fontWeight: 900,
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    color: BRAND.ink,
                    fontWeight: 950,
                    lineHeight: 1,
                    letterSpacing: "-0.055em",
                    mb: 2.4,
                    fontSize: { xs: "2.45rem", md: "4.7rem" },
                  }}
                >
                  Help artists upload their paintings to{" "}
                  <Box component="span" sx={{ color: BRAND.turquoiseDark }}>
                    Art For My Room
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontSize: { xs: "1rem", md: "1.12rem" },
                    lineHeight: 1.9,
                    maxWidth: 820,
                    mb: 3.5,
                  }}
                >
                  We are looking for a student to research emerging painters,
                  contact artists professionally, understand what would make them
                  upload their work, and help bring the first wave of founding
                  artists onto our AI-art platform.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 4 }}>
                  {[
                    "Market research",
                    "Artist outreach",
                    "Upload support",
                    "AI ideas welcome",
                    "Remote friendly",
                    "Portfolio experience",
                  ].map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        border: `1px solid ${alpha(BRAND.turquoise, 0.16)}`,
                        color: BRAND.ink,
                        fontWeight: 800,
                      }}
                    />
                  ))}
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    href="#apply"
                    variant="contained"
                    startIcon={<EmailIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 3,
                      py: 1.35,
                      fontWeight: 900,
                      background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.violet})`,
                      boxShadow: `0 18px 40px ${alpha(BRAND.turquoise, 0.26)}`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${BRAND.turquoiseDark}, ${BRAND.violet})`,
                      },
                    }}
                  >
                    Apply now
                  </Button>

                  <Button
                    href="#mission"
                    variant="outlined"
                    startIcon={<TravelExploreIcon />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 3,
                      py: 1.35,
                      fontWeight: 900,
                      color: BRAND.turquoiseDark,
                      borderColor: BRAND.turquoise,
                      backgroundColor: alpha(BRAND.white, 0.74),
                      "&:hover": {
                        borderColor: BRAND.turquoiseDark,
                        backgroundColor: BRAND.turquoiseSoft,
                      },
                    }}
                  >
                    See the mission
                  </Button>
                </Stack>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={5}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.95, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 6,
                    backgroundColor: alpha(BRAND.white, 0.9),
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: "0 24px 70px rgba(16,42,42,0.10)",
                  }}
                >
                  <Box
                    sx={{
                      p: 3.5,
                      borderRadius: 5,
                      color: "#FFFFFF",
                      background: `linear-gradient(150deg, ${BRAND.turquoiseDark}, ${BRAND.turquoise}, ${BRAND.violet})`,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        width: 150,
                        height: 150,
                        borderRadius: "50%",
                        backgroundColor: alpha("#FFFFFF", 0.16),
                        top: -45,
                        right: -45,
                      }}
                    />

                    <SchoolIcon sx={{ fontSize: 48, mb: 2 }} />

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 950,
                        lineHeight: 1.1,
                        letterSpacing: "-0.035em",
                        mb: 2,
                      }}
                    >
                      Artist Onboarding Market Research Assistant
                    </Typography>

                    <Typography sx={{ lineHeight: 1.8, opacity: 0.94, mb: 3 }}>
                      A student role for someone who enjoys art, people,
                      research, social media, AI tools and the early sparks of a
                      startup.
                    </Typography>

                    <Grid container spacing={2}>
                      {[
                        ["Type", "Paid project"],
                        ["Length", "4 to 6 weeks"],
                        ["Place", "Remote / London"],
                        ["Goal", "Artist uploads"],
                      ].map(([label, value]) => (
                        <Grid item xs={6} key={label}>
                          <Typography sx={{ fontSize: "0.78rem", opacity: 0.78 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontWeight: 900 }}>{value}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container id="mission" maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box textAlign="center" sx={{ mb: 5 }}>
          <Typography
            variant="h2"
            sx={{
              color: BRAND.ink,
              fontWeight: 950,
              letterSpacing: "-0.045em",
              fontSize: { xs: "2rem", md: "3rem" },
              mb: 1.5,
            }}
          >
            What you will actually do
          </Typography>

          <Typography
            sx={{
              color: BRAND.muted,
              lineHeight: 1.8,
              maxWidth: 760,
              mx: "auto",
            }}
          >
            Clear work, clear outcome. No vague “help the startup somehow”
            cloud. This is a proper little mission with a map.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {missionCards.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <MotionBox
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={{ duration: 0.38, delay: index * 0.05 }}
                sx={{ height: "100%" }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 5,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: "0 18px 45px rgba(16,42,42,0.06)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        color: BRAND.turquoiseDark,
                        backgroundColor: BRAND.turquoiseSoft,
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}
                    >
                      {item.title}
                    </Typography>

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.75, mb: 2 }}>
                      {item.text}
                    </Typography>

                    <Chip
                      label={item.outcome}
                      size="small"
                      sx={{
                        backgroundColor: BRAND.turquoisePale,
                        color: BRAND.turquoiseDark,
                        fontWeight: 800,
                      }}
                    />
                  </CardContent>
                </Card>
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box
        sx={{
          py: { xs: 7, md: 9 },
          background: `linear-gradient(180deg, #FFFFFF 0%, ${alpha(BRAND.turquoise, 0.08)} 100%)`,
          borderTop: `1px solid ${alpha(BRAND.turquoise, 0.12)}`,
          borderBottom: `1px solid ${alpha(BRAND.turquoise, 0.12)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: "100%",
                  borderRadius: 5,
                  border: `1px solid ${BRAND.border}`,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Chip
                  icon={<LightbulbIcon />}
                  label="AI and engineering ideas welcome"
                  sx={{
                    mb: 2,
                    backgroundColor: BRAND.turquoiseSoft,
                    color: BRAND.turquoiseDark,
                    fontWeight: 900,
                  }}
                />

                <Typography
                  variant="h4"
                  sx={{
                    color: BRAND.ink,
                    fontWeight: 950,
                    letterSpacing: "-0.035em",
                    mb: 1.5,
                  }}
                >
                  This is more than outreach.
                </Typography>

                <Typography sx={{ color: BRAND.muted, lineHeight: 1.85, mb: 3 }}>
                  We also want students who can spot product ideas. What would
                  make artists trust the upload process? What AI tools would be
                  genuinely helpful? What should the next feature be?
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    "Suggest better upload journeys",
                    "Research artist trust and motivation",
                    "Imagine AI certificate tools",
                    "Propose artist dashboards or search features",
                    "Help turn ideas into small experiments",
                  ].map((text) => (
                    <Stack key={text} direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: BRAND.turquoiseDark, mt: "2px" }} />
                      <Typography sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                        {text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {ideaCards.map((item, index) => (
                  <Grid item xs={12} sm={6} key={item.title}>
                    <MotionBox
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.16 }}
                      transition={{ duration: 0.34, delay: index * 0.04 }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          height: "100%",
                          borderRadius: 4,
                          border: `1px solid ${BRAND.border}`,
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        <Stack direction="row" spacing={1.6} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 3,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: BRAND.turquoiseDark,
                              backgroundColor: BRAND.turquoiseSoft,
                            }}
                          >
                            {item.icon}
                          </Box>

                          <Box>
                            <Typography
                              sx={{ color: BRAND.ink, fontWeight: 900, mb: 0.6 }}
                            >
                              {item.title}
                            </Typography>

                            <Typography
                              sx={{
                                color: BRAND.muted,
                                lineHeight: 1.65,
                                fontSize: "0.95rem",
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </MotionBox>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: BRAND.ink, color: "#FFFFFF", py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 5 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 950,
                letterSpacing: "-0.045em",
                fontSize: { xs: "2rem", md: "3rem" },
                mb: 1.5,
              }}
            >
              A simple 4-week sprint
            </Typography>

            <Typography
              sx={{
                color: alpha("#FFFFFF", 0.72),
                lineHeight: 1.8,
                maxWidth: 720,
                mx: "auto",
              }}
            >
              A focused structure that creates real results quickly: artist
              leads, artist conversations, uploads and product insight.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {timeline.map((item, index) => (
              <Grid item xs={12} md={3} key={item.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 5,
                    height: "100%",
                    backgroundColor: alpha("#FFFFFF", 0.08),
                    border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
                    color: "#FFFFFF",
                  }}
                >
                  <Typography sx={{ color: BRAND.gold, fontWeight: 950, mb: 1 }}>
                    {item.label}
                  </Typography>

                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                    {item.title}
                  </Typography>

                  <Typography sx={{ color: alpha("#FFFFFF", 0.74), lineHeight: 1.75 }}>
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container id="apply" maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 6,
                border: `1px solid ${BRAND.border}`,
                backgroundColor: BRAND.turquoisePale,
              }}
            >
              <Chip
                icon={<EmailIcon />}
                label="Apply by email form"
                sx={{
                  mb: 2,
                  backgroundColor: "#FFFFFF",
                  color: BRAND.turquoiseDark,
                  fontWeight: 900,
                }}
              />

              <Typography
                variant="h3"
                sx={{
                  color: BRAND.ink,
                  fontWeight: 950,
                  letterSpacing: "-0.04em",
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  mb: 1.5,
                }}
              >
                Send your application
              </Typography>

              <Typography sx={{ color: BRAND.muted, lineHeight: 1.85, mb: 3 }}>
                This form sends your details to Art For My Room using the same
                backend email route used by the opportunity subscription page.
                No giant application portal, no dusty bureaucracy cupboard.
              </Typography>

              <Stack spacing={1.5}>
                {[
                  {
                    icon: <PaidIcon />,
                    text: "Paid student project",
                  },
                  {
                    icon: <PaletteIcon />,
                    text: "Ideal for art, marketing, business, AI or tech students",
                  },
                  {
                    icon: <HandshakeIcon />,
                    text: "Help real artists join the platform",
                  },
                  {
                    icon: <WorkOutlineIcon />,
                    text: "Build startup and portfolio experience",
                  },
                ].map((item) => (
                  <Stack key={item.text} direction="row" spacing={1.2} alignItems="flex-start">
                    <Box sx={{ color: BRAND.turquoiseDark, mt: "2px" }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                      {item.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 6,
                border: `1px solid ${BRAND.border}`,
                backgroundColor: "#FFFFFF",
                boxShadow: "0 18px 50px rgba(16,42,42,0.06)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: BRAND.ink,
                  fontWeight: 950,
                  letterSpacing: "-0.02em",
                  mb: 2.5,
                }}
              >
                Student mission application
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Full name"
                    value={form.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="University / education provider"
                    value={form.university}
                    onChange={(e) => handleChange("university", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Course"
                    value={form.course}
                    onChange={(e) => handleChange("course", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Main interest"
                    value={form.roleInterest}
                    onChange={(e) => handleChange("roleInterest", e.target.value)}
                  >
                    {roleOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Availability"
                    value={form.availability}
                    onChange={(e) => handleChange("availability", e.target.value)}
                  >
                    {availabilityOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Preferred location"
                    value={form.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                  >
                    {locationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Portfolio, LinkedIn, Instagram or GitHub"
                    placeholder="Paste a link if you have one"
                    value={form.portfolio}
                    onChange={(e) => handleChange("portfolio", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Relevant experience"
                    placeholder="Example: I study marketing, I run an Instagram page, I paint, I have used AI tools, I have done research projects..."
                    value={form.experience}
                    onChange={(e) => handleChange("experience", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Any AI, engineering or art idea you would like to suggest?"
                    placeholder="Example: an artist upload dashboard, artwork certificate generator, AI description tool, artist search page..."
                    value={form.aiIdea}
                    onChange={(e) => handleChange("aiIdea", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Why would you like to join this project?"
                    value={form.whyJoin}
                    onChange={(e) => handleChange("whyJoin", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.rightToWork}
                        onChange={(e) => handleChange("rightToWork", e.target.checked)}
                        sx={{
                          color: BRAND.turquoise,
                          "&.Mui-checked": {
                            color: BRAND.turquoiseDark,
                          },
                        }}
                      />
                    }
                    label="I confirm that I can provide right-to-work information if selected for paid work."
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.consent}
                        onChange={(e) => handleChange("consent", e.target.checked)}
                        sx={{
                          color: BRAND.turquoise,
                          "&.Mui-checked": {
                            color: BRAND.turquoiseDark,
                          },
                        }}
                      />
                    }
                    label="I agree that Art For My Room can contact me about this student opportunity."
                  />
                </Grid>
              </Grid>

              {submitMessage.text && (
                <Alert
                  severity={submitMessage.type === "success" ? "success" : "error"}
                  sx={{ mt: 2.5, borderRadius: 3 }}
                >
                  {submitMessage.text}
                </Alert>
              )}

              {!formReady && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2.5,
                    borderRadius: 3,
                    backgroundColor: alpha(BRAND.turquoise, 0.08),
                    color: BRAND.ink,
                    "& .MuiAlert-icon": {
                      color: BRAND.turquoiseDark,
                    },
                  }}
                >
                  Required: name, email, university, right-to-work confirmation
                  and contact consent.
                </Alert>
              )}

              <Divider sx={{ my: 3, borderColor: BRAND.border }} />

              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 3,
                  py: 1.35,
                  fontWeight: 950,
                  backgroundColor: BRAND.turquoise,
                  color: "#083236",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: BRAND.turquoiseDark,
                    color: "#FFFFFF",
                    boxShadow: "none",
                  },
                }}
              >
                {submitting ? (
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <CircularProgress size={18} sx={{ color: "inherit" }} />
                    <span>Sending application...</span>
                  </Stack>
                ) : (
                  "Send application"
                )}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
